import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { TextareaAutosize } from '@mui/material'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { BaseModal } from 'components/organisms/modals/base-modal'
import { Label } from 'components/atoms/Label'

const validationSchema = Yup.object({
	body: Yup.string()
		.required('Please enter a message')
		.min(10, 'Message must be at least 10 characters')
		.max(1000, 'Message must be at most 1000 characters'),
})

type ContactHostFormData = Yup.InferType<typeof validationSchema>

export interface ContactHostModalProps {
	open: boolean
	onClose: () => void
	onSubmit: (body: string) => void
	loading: boolean
}

export function ContactHostModal({ open, onClose, onSubmit, loading }: ContactHostModalProps) {
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isValid, isDirty },
	} = useForm<ContactHostFormData>({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			body: '',
		},
	})

	useEffect(() => {
		if (!open) {
			reset()
		}
	}, [open, reset])

	const handleFormSubmit = (data: ContactHostFormData) => {
		onSubmit(data.body)
	}

	return (
		<BaseModal
			open={open}
			onClose={onClose}
			title="Contact Host"
			size="md"
			customSize="sm:max-w-[500px]"
			className="p-8"
			bodyClassName="h-[75vh] sm:h-fit sm:max-h-[75vh]"
			headerClassName="mb-4"
		>
			<div className="w-full mt-6">
				<form onSubmit={handleSubmit(handleFormSubmit)}>
					<div>
						<Label label="Message" className="block text-left" />
						<div className="mt-2">
							<TextareaAutosize
								className={clsx(
									'px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full',
									{
										'!border-secondary-red': !!errors.body,
									},
								)}
								minRows={6}
								placeholder="Write your message to the host..."
								{...register('body')}
							/>
							{errors.body && (
								<div className="text-secondary-red mt-2 text-sm">{errors.body.message}</div>
							)}
						</div>
					</div>

					<div className="flex justify-center mt-6">
						<button
							type="submit"
							disabled={loading || !isValid || !isDirty}
							className="secondary-button w-2/3"
						>
							{loading && (
								<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="mr-2" />
							)}
							Send Message
						</button>
					</div>
				</form>
			</div>
		</BaseModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface ContactHostModalManagedProps {
	onSubmit: (body: string) => void
	loading: boolean
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'contact-host': ContactHostModalManagedProps
	}
}

export function ManagedContactHostModal({
	onClose: managerOnClose,
	onSubmit,
	loading,
}: ContactHostModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(managerOnClose, 200)
	}

	return (
		<ContactHostModal
			open={open}
			onClose={handleClose}
			onSubmit={(body) => { onSubmit(body); handleClose() }}
			loading={loading}
		/>
	)
}


